import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Ship, ShieldCheck, FileText, Target, Eye, Handshake } from "lucide-react";
import { IMAGES } from "@/lib/constants";
import { useLanguage } from "@/i18n/LanguageContext";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const ICONS = [ShieldCheck, FileText, MapPin, Ship];

const About = () => {
  const { t } = useLanguage();
  const cards = t("about.compliance.cards");
  const opsList = t("about.ops.list");

  return (
    <main data-testid="about-page" className="pt-32">
      {/* CORPORATE BRIEF */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("about.kicker")}
          </p>
          <h1 className="font-display mt-3 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {t("about.titleA")} <span className="gold-text">{t("about.titleGold")}</span>{" "}
            {t("about.titleB")}
          </h1>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-300 sm:text-lg">
            {t("about.brief")}
          </p>
        </Reveal>
      </section>

      {/* COMPANY PROFILE */}
      <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8" data-testid="company-profile-section">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("about.profile.kicker")}
          </p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {t("about.profile.title")}
          </h2>
          <p className="mt-5 max-w-3xl leading-relaxed text-slate-400">{t("about.profile.body")}</p>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {[
            { icon: Target, title: t("about.profile.mission"), text: t("about.profile.missionText") },
            { icon: Eye, title: t("about.profile.vision"), text: t("about.profile.visionText") },
            { icon: Handshake, title: t("about.profile.why"), text: t("about.profile.whyText") },
          ].map((c, i) => (
            <Reveal key={c.title} delay={0.08 * i}>
              <div
                data-testid={`profile-card-${i}`}
                className="group h-full border border-white/10 bg-[#0d223f] p-7 transition-colors duration-500 hover:border-[#4a6cf7]/60"
              >
                <c.icon className="h-7 w-7 text-[#4a6cf7] transition-transform duration-500 group-hover:scale-110" />
                <h3 className="font-display mt-5 text-lg font-semibold text-white">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee />

      {/* OPERATIONS */}
      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8" data-testid="operations-section">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("about.ops.kicker")}
          </p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {t("about.ops.title")}
          </h2>
          <p className="mt-5 leading-relaxed text-slate-400">{t("about.ops.body")}</p>
          <ul className="mt-7 space-y-3 text-sm text-slate-300">
            {opsList.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#4a6cf7]" />
                {item}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="group relative overflow-hidden border border-[#4a6cf7]/25">
            <img
              src={IMAGES.operations}
              alt="Mining representatives reviewing documentation at loading dock"
              className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/80 via-transparent to-transparent" />
            <p className="font-mono2 absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
              {t("about.ops.caption")}
            </p>
          </div>
        </Reveal>
      </section>

      {/* LOGISTICS */}
      <section className="border-y border-[#4a6cf7]/15 bg-[#0d223f]" data-testid="logistics-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8">
          <Reveal className="order-2 lg:order-1">
            <div className="group relative overflow-hidden border border-[#4a6cf7]/25">
              <img
                src={IMAGES.shipping}
                alt="Container vessel on international export route"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a192f]/80 via-transparent to-transparent" />
              <p className="font-mono2 absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-[#9db4ff]">
                {t("about.logistics.caption")}
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
              {t("about.logistics.kicker")}
            </p>
            <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
              {t("about.logistics.title")}
            </h2>
            <p className="mt-5 leading-relaxed text-slate-400">{t("about.logistics.body")}</p>
          </Reveal>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="compliance-section">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
            {t("about.compliance.kicker")}
          </p>
          <h2 className="font-display mt-3 max-w-3xl text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
            {t("about.compliance.title")}
          </h2>
          <p className="mt-5 max-w-3xl leading-relaxed text-slate-400">{t("about.compliance.body")}</p>
        </Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={s.title} delay={0.08 * i}>
                <div
                  data-testid={`compliance-card-${i}`}
                  className="group h-full border border-white/10 bg-[#0d223f] p-7 transition-colors duration-500 hover:border-[#4a6cf7]/60"
                >
                  <Icon className="h-7 w-7 text-[#9db4ff] transition-transform duration-500 group-hover:scale-110" />
                  <h3 className="font-display mt-5 text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-col items-start justify-between gap-6 border border-[#4a6cf7]/30 bg-gradient-to-r from-[#4a6cf7]/10 to-transparent p-8 sm:flex-row sm:items-center">
            <p className="font-display max-w-xl text-lg font-medium text-white">
              {t("about.compliance.ctaText")}
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-rfq"
              className="group flex shrink-0 items-center gap-3 bg-gradient-to-r from-[#4a6cf7] to-[#2f49c6] px-7 py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-white transition-transform duration-300 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t("about.compliance.ctaBtn")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default About;
